import classNames from 'classnames';
import type { CSSProperties } from 'react';
import { FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Skin } from '#src/__generated__/globalTypes.ts';
import { Percentage } from '#src/atoms/icons/Percentage.tsx';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { BrandXS, BrandXXS } from '#src/atoms/typography/index.tsx';
import {
  skinEditionChipBackground,
  skinEditionChipClassName,
} from '#src/style/skinEditionChip.ts';
import type { Color, RGBColor } from '#src/style/types.ts';

const Root = styled(Horizontal).attrs({ center: true })`
  min-width: var(--quadruple-unit);
  min-height: var(--chip-small-height);
  gap: 1px;
  border-radius: 9999px;
  border: 1px solid var(--c-nd-400);
  background: linear-gradient(-30deg, var(--c-nd-200) 50%, var(--c-nd-100) 50%);
  padding: 0 2px;

  &.colored {
    --color: rgba(var(--rgb-color), 0.1);
    border-color: rgb(var(--rgb-color));
    background:
      linear-gradient(var(--color), var(--color)),
      linear-gradient(-30deg, var(--c-nd-200) 50%, var(--c-nd-100) 50%);
  }

  ${skinEditionChipBackground}
`;

type Props = {
  bonus: number;
  skin?: Nullable<Skin>;
  color?: Color;
  rgbColor?: RGBColor;
  large?: boolean;
  className?: string;
};

export const DumbBonusChip = ({
  bonus,
  skin,
  color,
  rgbColor,
  large,
  className,
}: Props) => {
  const isSkinEdition = !!skin && skin !== Skin.STANDARD;
  const resolvedColor: Color | undefined = isSkinEdition
    ? ('var(--c-black)' as Color)
    : color;
  const Value = large ? BrandXS : BrandXXS;

  return (
    <Root
      style={{ '--rgb-color': rgbColor } as CSSProperties}
      className={classNames(className, skinEditionChipClassName(skin), {
        colored: !!rgbColor && !isSkinEdition,
      })}
    >
      <Value bold color={resolvedColor}>
        <FormattedNumber value={bonus * 100} signDisplay="always" />
      </Value>
      <Percentage color={resolvedColor} />
    </Root>
  );
};
